import { geoStereographic } from "d3-geo";
import type {
  BodyPosition,
  CatalogConstellation,
  SkyCatalog,
  SkyObjectPosition,
  StarPosition,
} from "../core/types";

export interface SkyViewportState {
  centerAzimuthDeg: number;
  centerAltitudeDeg: number;
  fieldOfViewDeg: number;
}

export interface DrawSkyInput {
  canvas: HTMLCanvasElement;
  catalog: SkyCatalog;
  stars: StarPosition[];
  bodies: BodyPosition[];
  selected?: SkyObjectPosition;
  targetHrs: number[];
  viewport: SkyViewportState;
  showGuideLines: boolean;
  showBelowHorizon: boolean;
}

export interface CanvasHit {
  object: SkyObjectPosition;
  distancePx: number;
  x: number;
  y: number;
  hitRadius: number;
}

interface ProjectedObject {
  object: SkyObjectPosition;
  x: number;
  y: number;
}

export function drawSky(input: DrawSkyInput): CanvasHit[] {
  const {
    canvas,
    catalog,
    stars,
    bodies,
    selected,
    targetHrs,
    viewport,
    showGuideLines,
    showBelowHorizon,
  } = input;
  const rect = canvas.getBoundingClientRect();
  const ratio = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.floor(rect.width * ratio));
  canvas.height = Math.max(1, Math.floor(rect.height * ratio));

  const ctx = canvas.getContext("2d");
  if (!ctx) return [];
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

  const width = rect.width;
  const height = rect.height;
  drawBackground(ctx, width, height);

  const projection = geoStereographic()
    .rotate([-viewport.centerAzimuthDeg, -viewport.centerAltitudeDeg])
    .translate([width / 2, height / 2])
    .scale((height * 0.62) / Math.tan((viewport.fieldOfViewDeg * Math.PI) / 360))
    .clipAngle(118);

  if (showGuideLines) {
    drawAltitudeGrid(ctx, projection);
    drawAzimuthGrid(ctx, projection);
    drawMeridian(ctx, projection);
    drawHorizon(ctx, projection);
    drawGuideLabels(ctx, projection);
  }
  drawConstellations(ctx, catalog.constellations, stars, projection, selected, showBelowHorizon);

  const projected: ProjectedObject[] = [];
  const sortedStars = [...stars].sort((a, b) => b.magnitude - a.magnitude);
  const magnitudeLimit = magnitudeLimitForViewport(viewport.fieldOfViewDeg);
  for (const star of sortedStars) {
    const selectedStar = selected?.id === star.id;
    const inTargetList = targetHrs.includes(star.hr);
    if (!shouldDrawStar(star, magnitudeLimit, showBelowHorizon, selectedStar, inTargetList)) continue;
    const point = projectObject(projection, star);
    if (!point) continue;
    projected.push({ object: star, x: point[0], y: point[1] });
    drawStar(ctx, star, point[0], point[1], inTargetList, selectedStar);
  }

  for (const body of bodies) {
    const selectedBody = selected?.id === body.id;
    if (!showBelowHorizon && body.altitudeDeg < 0 && !selectedBody) continue;
    const point = projectObject(projection, body);
    if (!point) continue;
    projected.push({ object: body, x: point[0], y: point[1] });
    drawBody(ctx, body, point[0], point[1], selectedBody);
  }

  drawLabels(ctx, projected, selected, viewport);
  drawViewportReadout(ctx, width, height, viewport, magnitudeLimit, showBelowHorizon);

  return projected.map(({ object, x, y }) => ({
    object,
    distancePx: Number.POSITIVE_INFINITY,
    x,
    y,
    hitRadius: hitRadiusForObject(object),
  }));
}

export function nearestCanvasObject(hits: CanvasHit[], x: number, y: number): CanvasHit | undefined {
  let best: CanvasHit | undefined;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const hit of hits) {
    const distancePx = Math.hypot(hit.x - x, hit.y - y);
    if (distancePx > hit.hitRadius) continue;

    const brightnessBias = hit.object.kind === "star" ? hit.object.magnitude * 0.018 : -0.08;
    const score = distancePx / hit.hitRadius + brightnessBias;
    if (score < bestScore) {
      bestScore = score;
      best = { object: hit.object, distancePx, x: hit.x, y: hit.y, hitRadius: hit.hitRadius };
    }
  }
  return best;
}

function drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, "#07111f");
  gradient.addColorStop(0.46, "#101827");
  gradient.addColorStop(1, "#18131c");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = "rgba(143, 211, 255, 0.12)";
  ctx.lineWidth = 1;
  for (let i = 1; i <= 3; i += 1) {
    ctx.beginPath();
    ctx.ellipse(width / 2, height / 2, (width * i) / 7, (height * i) / 7, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function drawAltitudeGrid(
  ctx: CanvasRenderingContext2D,
  projection: ReturnType<typeof geoStereographic>,
): void {
  ctx.strokeStyle = "rgba(143, 211, 255, 0.12)";
  ctx.lineWidth = 1;
  for (const alt of [15, 30, 45, 60, 75]) {
    ctx.beginPath();
    let started = false;
    for (let az = 0; az <= 360; az += 3) {
      const point = projection([az, alt]);
      if (!point) {
        started = false;
        continue;
      }
      if (!started) {
        ctx.moveTo(point[0], point[1]);
        started = true;
      } else {
        ctx.lineTo(point[0], point[1]);
      }
    }
    ctx.stroke();
  }
}

function drawAzimuthGrid(
  ctx: CanvasRenderingContext2D,
  projection: ReturnType<typeof geoStereographic>,
): void {
  ctx.strokeStyle = "rgba(143, 211, 255, 0.1)";
  ctx.lineWidth = 1;
  for (let az = 0; az < 360; az += 45) {
    ctx.beginPath();
    let started = false;
    for (let alt = 0; alt <= 90; alt += 2) {
      const point = projection([az, alt]);
      if (!point) {
        started = false;
        continue;
      }
      if (!started) {
        ctx.moveTo(point[0], point[1]);
        started = true;
      } else {
        ctx.lineTo(point[0], point[1]);
      }
    }
    ctx.stroke();
  }
}

function drawHorizon(
  ctx: CanvasRenderingContext2D,
  projection: ReturnType<typeof geoStereographic>,
): void {
  ctx.strokeStyle = "rgba(241, 195, 108, 0.78)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  let started = false;
  for (let az = 0; az <= 360; az += 2) {
    const point = projection([az, 0]);
    if (!point) {
      started = false;
      continue;
    }
    if (!started) {
      ctx.moveTo(point[0], point[1]);
      started = true;
    } else {
      ctx.lineTo(point[0], point[1]);
    }
  }
  ctx.stroke();
}

function drawMeridian(
  ctx: CanvasRenderingContext2D,
  projection: ReturnType<typeof geoStereographic>,
): void {
  ctx.strokeStyle = "rgba(124, 214, 185, 0.28)";
  ctx.lineWidth = 1;
  for (const az of [0, 180]) {
    ctx.beginPath();
    let started = false;
    for (let alt = 0; alt <= 90; alt += 2) {
      const point = projection([az, alt]);
      if (!point) continue;
      if (!started) {
        ctx.moveTo(point[0], point[1]);
        started = true;
      } else {
        ctx.lineTo(point[0], point[1]);
      }
    }
    ctx.stroke();
  }
}

function drawConstellations(
  ctx: CanvasRenderingContext2D,
  constellations: CatalogConstellation[],
  stars: StarPosition[],
  projection: ReturnType<typeof geoStereographic>,
  selected?: SkyObjectPosition,
  showBelowHorizon = false,
): void {
  const starsByHr = new Map(stars.map((star) => [star.hr, star]));
  for (const constellation of constellations) {
    const active = selected?.id === `const:${constellation.iau}`;
    ctx.strokeStyle = active ? "rgba(255, 214, 137, 0.92)" : "rgba(142, 167, 198, 0.24)";
    ctx.lineWidth = active ? 2.2 : 1;
    for (const [startHr, endHr] of constellation.lines) {
      const start = starsByHr.get(startHr);
      const end = starsByHr.get(endHr);
      if (!start || !end) continue;
      if (!showBelowHorizon && start.altitudeDeg < 0 && end.altitudeDeg < 0) continue;
      const startPoint = projectObject(projection, start);
      const endPoint = projectObject(projection, end);
      if (!startPoint || !endPoint) continue;
      ctx.beginPath();
      ctx.moveTo(startPoint[0], startPoint[1]);
      ctx.lineTo(endPoint[0], endPoint[1]);
      ctx.stroke();
    }
  }
}

function drawGuideLabels(
  ctx: CanvasRenderingContext2D,
  projection: ReturnType<typeof geoStereographic>,
): void {
  for (const [label, az] of [
    ["N", 0],
    ["E", 90],
    ["S", 180],
    ["W", 270],
  ] as const) {
    const point = projection([az, 0]);
    if (point) drawTextLabel(ctx, label, point[0], point[1], "#f1c36c", "center");
  }

  const horizonLabel = firstProjectedPoint(projection, [[110, 0], [250, 0], [70, 0], [290, 0]]);
  if (horizonLabel) drawTextLabel(ctx, "Horizon", horizonLabel[0], horizonLabel[1] - 15, "#ffe0a6", "center");

  const meridianLabel = firstProjectedPoint(projection, [[180, 58], [0, 58], [180, 35], [0, 35]]);
  if (meridianLabel) drawTextLabel(ctx, "Meridian", meridianLabel[0] + 14, meridianLabel[1], "#a6f4de", "left");
}

function drawStar(
  ctx: CanvasRenderingContext2D,
  star: StarPosition,
  x: number,
  y: number,
  inTargetList: boolean,
  selected: boolean,
): void {
  const radius = Math.max(1.1, 4.6 - star.magnitude * 0.72);
  ctx.save();
  ctx.globalAlpha = star.altitudeDeg < 0 ? 0.2 : 1;
  if (selected) {
    drawSelectionHalo(ctx, x, y, radius, "#ffd37a");
  }
  if (star.magnitude <= 0.8 || selected || inTargetList) {
    ctx.fillStyle = selected ? "rgba(255, 221, 145, 0.16)" : "rgba(143, 211, 255, 0.14)";
    ctx.beginPath();
    ctx.arc(x, y, selected ? radius * 4 : radius * 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = spectralColor(star.star.spectralType);
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  if (inTargetList && !selected) {
    ctx.strokeStyle = "#7cd6b9";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, y, radius + 7, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

function drawBody(
  ctx: CanvasRenderingContext2D,
  body: BodyPosition,
  x: number,
  y: number,
  selected: boolean,
): void {
  const radius = body.id === "Moon" ? 8 : Math.max(4, 7 - body.magnitude * 0.28);
  ctx.save();
  ctx.globalAlpha = body.altitudeDeg < 0 ? 0.28 : 1;
  if (selected) {
    drawSelectionHalo(ctx, x, y, radius, "#ffd37a");
  }
  ctx.fillStyle = body.id === "Moon" ? "#f4edd2" : "#ffd889";
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  if (!selected) {
    ctx.strokeStyle = "rgba(255, 216, 137, 0.35)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(x, y, radius + 9, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

function drawLabels(
  ctx: CanvasRenderingContext2D,
  projected: ProjectedObject[],
  selected?: SkyObjectPosition,
  viewport?: SkyViewportState,
): void {
  const labelLimit = viewport ? labelMagnitudeLimitForViewport(viewport.fieldOfViewDeg) : 1.2;
  const labelObjects = projected
    .filter(({ object }) => object.id === selected?.id || object.magnitude <= labelLimit || object.kind === "body")
    .sort((a, b) => a.object.magnitude - b.object.magnitude)
    .slice(0, 26);

  ctx.font = "600 12px Inter, system-ui, sans-serif";
  ctx.textBaseline = "middle";
  for (const item of labelObjects) {
    const isSelected = item.object.id === selected?.id;
    ctx.fillStyle = isSelected ? "#ffe4a3" : "rgba(229, 239, 255, 0.86)";
    ctx.fillText(item.object.label, item.x + 10, item.y - 8);
  }
}

function drawViewportReadout(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  viewport: SkyViewportState,
  magnitudeLimit: number,
  showBelowHorizon: boolean,
): void {
  ctx.fillStyle = "rgba(7, 17, 31, 0.58)";
  ctx.fillRect(width - 238, height - 42, 222, 28);
  ctx.fillStyle = "rgba(229, 239, 255, 0.78)";
  ctx.font = "500 11px Inter, system-ui, sans-serif";
  ctx.fillText(
    `Az ${Math.round(viewport.centerAzimuthDeg)} | Alt ${Math.round(viewport.centerAltitudeDeg)} | FOV ${Math.round(viewport.fieldOfViewDeg)} | mag ${magnitudeLimit.toFixed(1)}${showBelowHorizon ? " | dim below" : ""}`,
    width - 228,
    height - 24,
  );
}

function drawTextLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  color: string,
  align: CanvasTextAlign,
): void {
  ctx.save();
  ctx.font = "800 12px Inter, system-ui, sans-serif";
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  const width = ctx.measureText(text).width;
  const left = align === "center" ? x - width / 2 : x - 4;
  ctx.fillStyle = "rgba(7, 17, 31, 0.68)";
  ctx.fillRect(left - 6, y - 11, width + 12, 22);
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
  ctx.restore();
}

function firstProjectedPoint(
  projection: ReturnType<typeof geoStereographic>,
  candidates: Array<[number, number]>,
): [number, number] | undefined {
  for (const candidate of candidates) {
    const point = projection(candidate);
    if (point) return point as [number, number];
  }
  return undefined;
}

function shouldDrawStar(
  star: StarPosition,
  magnitudeLimit: number,
  showBelowHorizon: boolean,
  selected: boolean,
  inTargetList: boolean,
): boolean {
  if (selected || inTargetList) return true;
  if (!showBelowHorizon && star.altitudeDeg < 0) return false;
  return star.magnitude <= magnitudeLimit;
}

function magnitudeLimitForViewport(fieldOfViewDeg: number): number {
  if (fieldOfViewDeg <= 45) return 6.6;
  if (fieldOfViewDeg <= 70) return 6.0;
  if (fieldOfViewDeg <= 95) return 5.4;
  if (fieldOfViewDeg <= 115) return 4.9;
  return 4.4;
}

function labelMagnitudeLimitForViewport(fieldOfViewDeg: number): number {
  if (fieldOfViewDeg <= 45) return 2.2;
  if (fieldOfViewDeg <= 70) return 1.8;
  if (fieldOfViewDeg <= 95) return 1.35;
  return 1.05;
}

function hitRadiusForObject(object: SkyObjectPosition): number {
  if (object.kind === "body") return 30;
  if (object.kind === "constellation") return 24;
  return Math.max(9, Math.min(24, 24 - object.magnitude * 2.1));
}

function drawSelectionHalo(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: string,
): void {
  ctx.save();
  const haloRadius = Math.max(22, radius + 18);
  const ringRadius = Math.max(13, radius + 9);
  const glow = ctx.createRadialGradient(x, y, radius + 1, x, y, haloRadius);
  glow.addColorStop(0, rgbaFromHex(color, 0.22));
  glow.addColorStop(0.52, rgbaFromHex(color, 0.11));
  glow.addColorStop(1, rgbaFromHex(color, 0));

  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(x, y, haloRadius, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = rgbaFromHex(color, 0.42);
  ctx.lineWidth = 1.15;
  ctx.beginPath();
  ctx.arc(x, y, ringRadius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function rgbaFromHex(hex: string, alpha: number): string {
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) return hex;
  const value = Number.parseInt(normalized, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function projectObject(
  projection: ReturnType<typeof geoStereographic>,
  object: SkyObjectPosition,
): [number, number] | null {
  return projection([object.azimuthDeg, object.altitudeDeg]) as [number, number] | null;
}

function spectralColor(spectralType: string): string {
  switch (spectralType.trimStart()[0]?.toUpperCase()) {
    case "O":
    case "B":
      return "#cbd9ff";
    case "A":
      return "#f4f8ff";
    case "F":
      return "#fff1cf";
    case "G":
      return "#ffdfa2";
    case "K":
      return "#ffc17a";
    case "M":
      return "#ff9277";
    default:
      return "#ffffff";
  }
}
